import React, { useEffect, useMemo, useState } from "react";
import {
  Center,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { BarChart } from "@mantine/charts";
import {
  getAllDistributors,
  getDistributorOrgs,
  getScopeDistribution,
} from "@utils/api-communication/RestApiCli";
import { Organization } from "types/orgTypes";
import { ScopeDistData, ScopeRow, ScopeUnit, toScopeDistData } from "./distributionUtils";

interface DistributorLike {
  distId: string;
  name?: string;
}

const EMPTY_DIST: ScopeDistData = { orgCounts: [], productCounts: [] };

interface ScopeBarChartProps {
  data: ScopeRow[];
  unit: ScopeUnit;
  emptyText?: string;
}

const ScopeBarChart: React.FC<ScopeBarChartProps> = ({ data, unit, emptyText }) => {
  if (!data.length) {
    return (
      <Text size="sm" c="dimmed" py="md">
        {emptyText ?? "Nothing to show."}
      </Text>
    );
  }
  const height = Math.max(220, Math.min(600, data.length * 32 + 60));
  return (
    <BarChart
      h={height}
      data={data}
      dataKey="scope"
      orientation="vertical"
      yAxisProps={{ width: 140, interval: 0 }}
      withBarValueLabel
      barChartProps={{ layout: "vertical" }}
      series={[
        {
          name: "count",
          label: unit === "orgs" ? "Organizations" : "Products",
          color: "teal.6",
        },
      ]}
    />
  );
};

const ScopeDistribution: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string | null>("overall");
  const [unit, setUnit] = useState<ScopeUnit>("orgs");

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [distributors, setDistributors] = useState<DistributorLike[]>([]);

  const [overall, setOverall] = useState<ScopeDistData>(EMPTY_DIST);
  const [overallLoading, setOverallLoading] = useState(true);

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [orgData, setOrgData] = useState<ScopeDistData>(EMPTY_DIST);
  const [orgLoading, setOrgLoading] = useState(false);

  const [selectedDistId, setSelectedDistId] = useState<string | null>(null);
  const [distData, setDistData] = useState<ScopeDistData>(EMPTY_DIST);
  const [distLoading, setDistLoading] = useState(false);

  // Selectors only; the scope counts come from the backend endpoint.
  useEffect(() => {
    let cancelled = false;
    getDistributorOrgs(
      (res) => !cancelled && setOrgs(Array.isArray(res?.data) ? res.data : []),
      () => !cancelled && setOrgs([]),
    );
    getAllDistributors(
      {},
      (res) => !cancelled && setDistributors(Array.isArray(res?.data) ? res.data : []),
      () => !cancelled && setDistributors([]),
    );
    setOverallLoading(true);
    getScopeDistribution(
      {},
      (res) => {
        if (cancelled) return;
        setOverall(toScopeDistData(res?.data));
        setOverallLoading(false);
      },
      () => {
        if (cancelled) return;
        setOverall(EMPTY_DIST);
        setOverallLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedOrgId) {
      setOrgData(EMPTY_DIST);
      setOrgLoading(false);
      return;
    }
    let cancelled = false;
    setOrgLoading(true);
    getScopeDistribution(
      { orgId: selectedOrgId },
      (res) => {
        if (cancelled) return;
        setOrgData(toScopeDistData(res?.data));
        setOrgLoading(false);
      },
      () => {
        if (cancelled) return;
        setOrgData(EMPTY_DIST);
        setOrgLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [selectedOrgId]);

  useEffect(() => {
    if (!selectedDistId) {
      setDistData(EMPTY_DIST);
      setDistLoading(false);
      return;
    }
    let cancelled = false;
    setDistLoading(true);
    getScopeDistribution(
      { distId: selectedDistId },
      (res) => {
        if (cancelled) return;
        setDistData(toScopeDistData(res?.data));
        setDistLoading(false);
      },
      () => {
        if (cancelled) return;
        setDistData(EMPTY_DIST);
        setDistLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [selectedDistId]);

  const overallRows = unit === "orgs" ? overall.orgCounts : overall.productCounts;
  const distRows = unit === "orgs" ? distData.orgCounts : distData.productCounts;

  const orgOptions = useMemo(
    () =>
      orgs
        .map((o) => ({
          value: o.orgId,
          label: o.name ? `${o.name} (${o.orgId})` : o.orgId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [orgs],
  );

  const distOptions = useMemo(
    () =>
      distributors
        .map((d) => ({
          value: d.distId,
          label: d.name ? `${d.name} (${d.distId})` : d.distId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [distributors],
  );

  return (
    <Paper withBorder radius="md" p="md">
      <Group justify="space-between" align="center" mb="sm">
        <Title order={4}>Scope Distribution</Title>
        {activeTab !== "byOrg" && (
          <SegmentedControl
            size="xs"
            value={unit}
            onChange={(v) => setUnit(v as ScopeUnit)}
            data={[
              { value: "orgs", label: "By Organization" },
              { value: "products", label: "By Product" },
            ]}
          />
        )}
      </Group>
      <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="overall">Overall</Tabs.Tab>
          <Tabs.Tab value="byOrg">By Organization</Tabs.Tab>
          <Tabs.Tab value="byDist">By Distributor</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overall" pt="md">
          {overallLoading ? (
            <Center py="md">
              <Loader />
            </Center>
          ) : (
            <ScopeBarChart
              data={overallRows}
              unit={unit}
              emptyText={
                unit === "orgs"
                  ? "No organizations to chart."
                  : "No products with scope info to chart."
              }
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="byOrg" pt="md">
          <Stack gap="sm">
            <Select
              label="Organization"
              placeholder="Select an organization"
              data={orgOptions}
              value={selectedOrgId}
              onChange={setSelectedOrgId}
              searchable
              clearable
              nothingFoundMessage="No organizations"
            />
            {orgLoading ? (
              <Center py="md">
                <Loader />
              </Center>
            ) : selectedOrgId ? (
              <ScopeBarChart
                data={orgData.productCounts}
                unit="products"
                emptyText="This organization has no products with scope info."
              />
            ) : (
              <Text size="sm" c="dimmed">
                Select an organization to see its product scope distribution.
              </Text>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="byDist" pt="md">
          <Stack gap="sm">
            <Select
              label="Distributor"
              placeholder="Select a distributor"
              data={distOptions}
              value={selectedDistId}
              onChange={setSelectedDistId}
              searchable
              clearable
              nothingFoundMessage="No distributors"
            />
            {distLoading ? (
              <Center py="md">
                <Loader />
              </Center>
            ) : selectedDistId ? (
              <ScopeBarChart
                data={distRows}
                unit={unit}
                emptyText="No matching scopes for this distributor."
              />
            ) : (
              <Text size="sm" c="dimmed">
                Select a distributor to see scope distribution across its organizations.
              </Text>
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Paper>
  );
};

export default ScopeDistribution;
